import { NewJoinersAndAllocationsModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsModal';
import { DataCustomDetailTct } from '@dealstack/shared/lib/generated';
import { useContext, useEffect, useState } from 'react';
import type {
	KycCertificationRequirementFlags,
	KycProofRequirementFlags,
} from '@dealstack/shared/lib/common/business/kycCertification';

export type NewJoinersAndAllocationsFormTemplate = {
	id: string;
	fileId?: string | null;
	fileName?: string | null;
	name: string;
	instruction?: string | null;
	type: DataCustomDetailTct['type'];
	local?: boolean;
};

/**
 * Whether the modal holds the programme's own proof requirements. Until it does, the form reads
 * all-off — values nobody chose — so the step neither shows them as the programme's nor saves them.
 */
export type NewJoinersAndAllocationsProofsStatus = 'loading' | 'loaded' | 'unavailable';

/** A proof requirement travels with the certification it owns, so the two live in one group. */
export type NewJoinersAndAllocationsProofValues = {
	individualProofs: Pick<
		KycProofRequirementFlags,
		'isProofOfIdentityRequired' | 'isProofOfAddressRequired'
	> &
		Pick<
			KycCertificationRequirementFlags,
			'isProofOfIdentityCertificationRequired' | 'isProofOfAddressCertificationRequired'
		>;
	vehicleProofs: Pick<KycProofRequirementFlags, 'isProofOfInvestmentVehicleRequired'> &
		Pick<KycCertificationRequirementFlags, 'isProofOfInvestmentVehicleCertificationRequired'>;
};

export type NewJoinersAndAllocationsProofGroup = keyof NewJoinersAndAllocationsProofValues;

export type NewJoinersAndAllocationsKycFormValues = NewJoinersAndAllocationsProofValues & {
	individualFormsTemplates: NewJoinersAndAllocationsFormTemplate[];
	pivFormsTemplates: NewJoinersAndAllocationsFormTemplate[];
};

export const useNewJoinersAndAllocationsKYCController = () => {
	const { kycForm, setDisableContinue, programmeProofsStatus } = useContext(
		NewJoinersAndAllocationsModalContext
	);

	const [showHowToProvideDocsModal, setShowHowToProvideDocsModal] = useState(false);
	const [isPEPModalVisible, setPEPModalVisible] = useState(false);

	useEffect(() => {
		const formContracts = kycForm.getValues();
		const shouldDisableContinue =
			formContracts?.individualFormsTemplates?.some((form) => form.name.trim() === '') ||
			formContracts?.pivFormsTemplates?.some((form) => form.name.trim() === '');
		setDisableContinue(shouldDisableContinue);

		return () => {
			setDisableContinue(false);
		};
	}, [kycForm.watch()]);

	return {
		kycForm,
		programmeProofsStatus,
		isPEPModalVisible,
		showHowToProvideDocsModal,
		setShowHowToProvideDocsModal,
		setPEPModalVisible,
	};
};
